export const GUEST_CART_KEY = 'kadolar_guest_cart_v1';
export const GUEST_CART_UPDATED_AT_KEY = 'kadolar_guest_cart_updated_at_v1';

export const normalizeOptionIds = (raw) => {
  if (!Array.isArray(raw)) return [];
  const out = [];
  const seen = new Set();
  for (const v of raw) {
    const n = Number(v);
    const i = Number.isFinite(n) ? Math.floor(n) : null;
    if (!i || i <= 0) continue;
    if (seen.has(i)) continue;
    seen.add(i);
    out.push(i);
  }
  out.sort((a, b) => a - b);
  return out;
};

export const normalizeUploadIds = (raw) => {
  if (!Array.isArray(raw)) return [];
  const out = [];
  const seen = new Set();
  for (const v of raw) {
    const n = Number(v);
    const i = Number.isFinite(n) ? Math.floor(n) : null;
    if (!i || i <= 0) continue;
    if (seen.has(i)) continue;
    seen.add(i);
    out.push(i);
  }
  out.sort((a, b) => a - b);
  return out;
};

export const buildGuestCartItemKey = (productId, optionIds, uploadIds, fieldValues) => {
  const pid = Number(productId);
  const ids = normalizeOptionIds(optionIds);
  const opt = ids.length ? ids.join('-') : '';
  const ups = normalizeUploadIds(uploadIds);
  const up = ups.length ? `u:${ups.join('-')}` : '';
  let fv = '';
  try {
    if (fieldValues && typeof fieldValues === 'object' && !Array.isArray(fieldValues)) {
      const keys = Object.keys(fieldValues).sort();
      const parts = [];
      for (const k of keys) {
        const v = String(fieldValues[k] ?? '').trim();
        if (v) parts.push(`${k}=${v}`);
      }
      if (parts.length) fv = `f:${parts.join('|')}`;
    }
  } catch {}
  let base = '';
  if (opt && up) base = `${Number.isFinite(pid) ? pid : ''}:${opt}|${up}`;
  else if (opt) base = `${Number.isFinite(pid) ? pid : ''}:${opt}`;
  else if (up) base = `${Number.isFinite(pid) ? pid : ''}:|${up}`;
  else base = `${Number.isFinite(pid) ? pid : ''}:`;
  return fv ? `${base}|${fv}` : base;
};

export const readGuestCart = () => {
  try {
    const raw = localStorage.getItem(GUEST_CART_KEY);
    if (!raw) return { version: 1, items: [], updated_at: 0 };
    const parsed = JSON.parse(raw);
    const items = Array.isArray(parsed?.items) ? parsed.items : [];
    const updatedAt = Number(parsed?.updated_at) || 0;
    return { version: 1, items, updated_at: updatedAt };
  } catch (_) {
    return { version: 1, items: [], updated_at: 0 };
  }
};

export const writeGuestCart = (cart) => {
  try {
    const next = cart && typeof cart === 'object' ? cart : { version: 1, items: [], updated_at: 0 };
    localStorage.setItem(GUEST_CART_KEY, JSON.stringify(next));
    localStorage.setItem(GUEST_CART_UPDATED_AT_KEY, String(Date.now()));
  } catch (_) {}
};

export const getGuestCartItems = () => readGuestCart().items || [];

export const getGuestCartCount = () => {
  const items = getGuestCartItems();
  return items.reduce((sum, it) => sum + (Number(it?.quantity) || 0), 0);
};

export const upsertGuestCartItem = ({ productId, quantity, customFeatureOptionIds, customUploadIds, customUploadMeta, customFieldValues }) => {
  const pid = Number(productId);
  if (!Number.isFinite(pid) || pid <= 0) return null;
  const qty = Math.max(1, Math.floor(Number(quantity) || 1));
  const ids = normalizeOptionIds(customFeatureOptionIds);
  const uploadIds = normalizeUploadIds(customUploadIds);
  const fieldValues = (customFieldValues && typeof customFieldValues === 'object' && !Array.isArray(customFieldValues)) ? customFieldValues : {};
  const key = buildGuestCartItemKey(pid, ids, uploadIds, fieldValues);

  const cart = readGuestCart();
  const items = Array.isArray(cart.items) ? cart.items.slice() : [];
  const idx = items.findIndex((it) => String(it?.id) === key);
  const now = Date.now();
  const meta = Array.isArray(customUploadMeta) ? customUploadMeta.filter((x) => x && Number.isFinite(Number(x.id))) : [];
  if (idx >= 0) {
    const prev = items[idx] || {};
    items[idx] = {
      ...prev,
      id: key,
      product_id: pid,
      quantity: Math.max(1, Math.floor((Number(prev.quantity) || 0) + qty)),
      custom_feature_option_ids: ids,
      custom_upload_ids: uploadIds.length ? uploadIds : normalizeUploadIds(prev.custom_upload_ids),
      custom_upload_meta: meta.length ? meta : (Array.isArray(prev.custom_upload_meta) ? prev.custom_upload_meta : []),
      custom_field_values: Object.keys(fieldValues).length ? fieldValues : (prev.custom_field_values || {}),
      updated_at: now,
    };
    if (uploadIds.length) {
      items[idx].custom_upload_ids = uploadIds;
      if (meta.length) items[idx].custom_upload_meta = meta;
    }
  } else {
    items.push({
      id: key,
      product_id: pid,
      quantity: qty,
      custom_feature_option_ids: ids,
      custom_upload_ids: uploadIds,
      custom_upload_meta: meta,
      custom_field_values: fieldValues,
      created_at: now,
      updated_at: now,
    });
  }
  const next = { version: 1, items, updated_at: now };
  writeGuestCart(next);
  return key;
};

export const updateGuestCartItem = (itemId, { quantity, customFeatureOptionIds, customUploadIds, customUploadMeta, customFieldValues } = {}) => {
  const id = String(itemId || '');
  if (!id) return false;
  const cart = readGuestCart();
  const items = Array.isArray(cart.items) ? cart.items.slice() : [];
  const idx = items.findIndex((it) => String(it?.id) === id);
  if (idx < 0) return false;
  const prev = items[idx] || {};
  const pid = Number(prev.product_id);
  if (!Number.isFinite(pid) || pid <= 0) return false;
  const nextQty = quantity === undefined ? (Number(prev.quantity) || 1) : Math.max(1, Math.floor(Number(quantity) || 1));
  const nextIds = customFeatureOptionIds === undefined ? normalizeOptionIds(prev.custom_feature_option_ids) : normalizeOptionIds(customFeatureOptionIds);
  const nextUploadIds = customUploadIds === undefined ? normalizeUploadIds(prev.custom_upload_ids) : normalizeUploadIds(customUploadIds);
  const nextMeta = customUploadMeta === undefined ? (Array.isArray(prev.custom_upload_meta) ? prev.custom_upload_meta : []) : (Array.isArray(customUploadMeta) ? customUploadMeta : []);
  const nextFieldValues = customFieldValues === undefined ? (prev.custom_field_values || {}) : (customFieldValues && typeof customFieldValues === 'object' ? customFieldValues : {});
  const nextId = buildGuestCartItemKey(pid, nextIds, nextUploadIds, nextFieldValues);
  const now = Date.now();

  const mergedIdx = items.findIndex((it, i) => i !== idx && String(it?.id) === nextId);
  if (mergedIdx >= 0) {
    const merged = items[mergedIdx] || {};
    items[mergedIdx] = {
      ...merged,
      id: nextId,
      product_id: pid,
      quantity: Math.max(1, Math.floor((Number(merged.quantity) || 0) + nextQty)),
      custom_feature_option_ids: nextIds,
      custom_upload_ids: nextUploadIds.length ? nextUploadIds : normalizeUploadIds(merged.custom_upload_ids),
      custom_upload_meta: nextMeta.length ? nextMeta : (Array.isArray(merged.custom_upload_meta) ? merged.custom_upload_meta : []),
      custom_field_values: Object.keys(nextFieldValues).length ? nextFieldValues : (merged.custom_field_values || {}),
      updated_at: now,
    };
    items.splice(idx, 1);
  } else {
    items[idx] = {
      ...prev,
      id: nextId,
      product_id: pid,
      quantity: nextQty,
      custom_feature_option_ids: nextIds,
      custom_upload_ids: nextUploadIds,
      custom_upload_meta: nextMeta,
      custom_field_values: nextFieldValues,
      updated_at: now,
    };
  }
  writeGuestCart({ version: 1, items, updated_at: now });
  return true;
};

export const removeGuestCartItem = (itemId) => {
  const id = String(itemId || '');
  if (!id) return false;
  const cart = readGuestCart();
  const items = Array.isArray(cart.items) ? cart.items.slice() : [];
  const nextItems = items.filter((it) => String(it?.id) !== id);
  if (nextItems.length === items.length) return false;
  writeGuestCart({ version: 1, items: nextItems, updated_at: Date.now() });
  return true;
};

export const clearGuestCart = () => {
  writeGuestCart({ version: 1, items: [], updated_at: Date.now() });
};

export const savePendingUploads = (productId, grouped) => {
  try {
    const pid = Number(productId);
    if (!Number.isFinite(pid) || pid <= 0) return;
    localStorage.setItem(`kadolar_pending_uploads_${pid}`, JSON.stringify(grouped || {}));
  } catch {}
};
export const loadPendingUploads = (productId) => {
  try {
    const pid = Number(productId);
    if (!Number.isFinite(pid) || pid <= 0) return null;
    const raw = localStorage.getItem(`kadolar_pending_uploads_${pid}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch { return null; }
};
export const clearPendingUploads = (productId) => {
  try {
    const pid = Number(productId);
    if (!Number.isFinite(pid) || pid <= 0) return;
    localStorage.removeItem(`kadolar_pending_uploads_${pid}`);
  } catch {}
};

export const createCartMergePlan = (serverItems, guestItems) => {
  const server = Array.isArray(serverItems) ? serverItems : [];
  const guest = Array.isArray(guestItems) ? guestItems : [];
  const serverKeys = new Set(
    server.map((it) => {
      const pid = it?.product?.id ?? it?.product_id ?? it?.productId;
      return buildGuestCartItemKey(pid, it?.custom_feature_option_ids, it?.custom_upload_ids, it?.custom_field_values);
    }).filter(Boolean)
  );
  const actions = [];
  for (const g of guest) {
    const pid = g?.product_id ?? g?.productId ?? g?.product?.id;
    const qty = Math.max(1, Math.floor(Number(g?.quantity) || 1));
    const ids = normalizeOptionIds(g?.custom_feature_option_ids);
    const ups = normalizeUploadIds(g?.custom_upload_ids);
    const key = buildGuestCartItemKey(pid, ids, ups, g?.custom_field_values);
    if (!key) continue;
    actions.push({
      key,
      product_id: Number(pid),
      quantity: qty,
      custom_feature_option_ids: ids,
      custom_upload_ids: ups,
      merges_into_existing: serverKeys.has(key),
    });
  }
  return actions;
};


// K3-2 one-time migration for old keys without f:
export const migrateGuestCartKeys = () => { try { const c=readGuestCart(); let changed=false; for(const it of c.items){ const correct=buildGuestCartItemKey(it.product_id, it.custom_feature_option_ids, it.custom_upload_ids, it.custom_field_values); if(it.id!==correct){ it.id=correct; changed=true; } } if(changed) writeGuestCart(c); } catch {} };

try{ migrateGuestCartKeys(); }catch{}

